import React, { useState } from 'react';
import { AccountReceivableSituation } from '@/core/domain/financial/AccountReceivable/AccountReceivable.types';
import { useDisclosure } from '@/hooks';
import { useAccountReceivableContext } from '@/pages/FinancialPages/BillToReceivePage/ReleasesPage/ReleasesDetailPage';
import { Button, Dropdown, Menu, Modal, notification } from 'antd';
import { FaAngleDown } from 'react-icons/fa';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import AccountReceivablePartiallyInstallmentModal from '../AccountReceivablePartiallyInstallmentModal';
import AccountReceivableRenegotiateInstallment from '../AccountReceivableRenegotiateInstallment';
import AccountReceivableReverseInstallmentModal from '../AccountReceivableReverseInstallmentModal';
import AccountReceivableSingleInstallmentModal from '../AccountReceivableSingleInstallmentModal';
import AccountReceivableSplitInstallment from '../AccountReceivableSplitInstallment';
import { generateBulletByAccountReceivable } from '@/core/domain/financial/Boletos/Boletos.hooks';
import CustomerSimplifiedRegisterModal from '@/components/Customers/CustomerSimplifiedRegisterModal';
import InsurerSimplifiedRegisterModal from '@/components/Insurers/InsurerSimplifiedRegisterModal';
import { useInsurerMutation } from '@/core/domain/people/Insurers/Insurers.hooks';
import { applyMask } from '@/utils/mask';
import { cnpjMask } from '@/utils/mask/commonMasks';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import ButtonBack from '@/components/_common/ButtonBack';
import { BankAccount } from '@/core/domain/enterprise/BankAccounts/BankAccounts.types';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';
import { UseDisclosureActions } from '@/hooks/useDisclosure';
import { useAccountReceivableListGrouping } from '@/core/domain/financial/AccountReceivable/AccountReceivable.hooks';
import AccountReceivableListGroupingModal from '../AccountReceivableListGroupingModal';
import { useCustomerContactsMutation } from '@/core/domain/people/CustomerContacts/CustomerContacts.hooks';
import { useCustomerReduced } from '@/core/domain/people/Customers/Customers.hooks';
import usePageSearchHistory from '@/hooks/pageSearchHistory';
import AccountReceivableTransactionCancel from './AccountReceivableTEFCancel';

interface Props {
  disabledBackButton: boolean;
  bankAccount?: BankAccount;
  handleSave: () => Promise<void>;
}

const AccountReceivableFooter: React.FC<Props> = ({
  disabledBackButton,
  bankAccount,
  handleSave,
}) => {
  const intl = useIntl();
  const history = useHistory();
  const { accountReceivable, currentInstallment } =
    useAccountReceivableContext();
  const installment =
    accountReceivable?.installmentReceivables[currentInstallment];
  const [singleSettleOpened, singleSettleAction] = useDisclosure();
  const [partiallySettleOpened, partiallySettleAction] = useDisclosure();
  const [reverseInstallmentOpened, reverseInstallmentAction] = useDisclosure();
  const [modalListGrouping, modalListGroupingAction] = useDisclosure();

  const { getSearch } = usePageSearchHistory();
  const path = '/app/financial/billToReceiver/releaseBillToReceiver';
  const search = getSearch(path) || '';
  const pathWithSearch = `${path}${search}`;

  const isStoneOrder = !!accountReceivable?.isStoneOrder;

  const [customerSimplifiedRegisterOpened, customerSimplifiedRegisterAction] =
    useDisclosure();
  const [insurerSimplifiedRegisterOpened, insurerSimplifiedRegisterAction] =
    useDisclosure();

  const [loadingButtons, setLoadingButtons] = useState({
    parcel: false,
    renegotiate: false,
    settleInstallment: false,
  });

  const generateBulletMutation = generateBulletByAccountReceivable();

  const customerMutation = useCustomerReduced();
  const customerContactsMutation = useCustomerContactsMutation();
  const insurerMutation = useInsurerMutation();
  const listGrouping = useAccountReceivableListGrouping(accountReceivable?.id);

  const handleVerifyEmail = async (customerId?: number, insurerId?: number) => {
    let hasEmail = false;

    if (accountReceivable) {
      if (installment) {
        if (customerId) {
          const contacts = await customerContactsMutation.mutateAsync(
            accountReceivable.customerId,
          );

          hasEmail = !!contacts
            ?.map((item) =>
              item.detailList.filter((det) => det.contactType === 'EMAIL'),
            )
            .flat()[0];
        } else if (insurerId) {
          const response = await insurerMutation.mutateAsync(
            accountReceivable.insurerId,
          );

          hasEmail = !!response.email;
        }

        if (!hasEmail) {
          Modal.confirm({
            title: 'Importante!',
            content: (
              <div style={{ whiteSpace: 'break-spaces' }}>
                {`Opa! Vimos que ${
                  customerId ? 'o cliente' : 'a seguradora'
                } está faltando o email.\nInformando o email o boleto será enviado de forma automática! `}
              </div>
            ),
            okText: 'Cadastrar email',
            onOk: () => {
              if (customerId) {
                customerSimplifiedRegisterAction.open();
              } else {
                insurerSimplifiedRegisterAction.open();
              }
            },
            cancelText: 'Somente incluir o boleto',
            onCancel: async () => {
              try {
                await generateBulletMutation.mutateAsync({
                  installmentReceivableId: installment.id,
                  bankAccountId: installment.bankAccountId,
                  accountReceivableId: installment.accountReceivableId,
                });
              } catch {}
            },
            cancelButtonProps: { type: 'primary' },
            width: 600,
            focusTriggerAfterClose: true,
          });
        } else {
          await generateBulletMutation.mutateAsync({
            installmentReceivableId: installment.id,
            bankAccountId: installment.bankAccountId,
            accountReceivableId: installment.accountReceivableId,
          });
        }
      }
    }
  };

  const handleGenerateBullet = async () => {
    if (accountReceivable) {
      if (accountReceivable.customerId) {
        const response = await customerMutation.mutateAsync(
          accountReceivable.customerId,
        );

        if (!response.cpfCnpj) {
          notification.warning({
            message:
              'Para a inclusão do boleto o cpf/cnpj do cliente é obrigatório',
          });

          return customerSimplifiedRegisterAction.open();
        }
      } else if (accountReceivable.insurerId) {
        const response = await insurerMutation.mutateAsync(
          accountReceivable.insurerId,
        );

        if (!response.cnpj) {
          notification.warning({
            message:
              'Para a inclusão do boleto o cnpj da seguradora é obrigatório',
          });

          return customerSimplifiedRegisterAction.open();
        }
      }

      if (accountReceivable.companyId !== bankAccount?.companyId) {
        Modal.confirm({
          title: 'Importante!',
          content: `O boleto será emitido para o cedente de CNPJ: ${applyMask(
            bankAccount?.companyCnpj,
            cnpjMask,
          )} referente a conta bancaria selecionada no qual se difere do CNPJ vinculado a esse recebimento`,
          okText: 'Continuar',
          onOk: async () => {
            if (accountReceivable.customerId) {
              handleVerifyEmail(accountReceivable.customerId);
            } else if (accountReceivable.insurerId) {
              handleVerifyEmail(undefined, accountReceivable.insurerId);
            }
          },
          cancelText: 'Cancelar',
          width: 600,
          focusTriggerAfterClose: true,
        });
      } else {
        if (accountReceivable.customerId) {
          handleVerifyEmail(accountReceivable.customerId);
        } else if (accountReceivable.insurerId) {
          handleVerifyEmail(undefined, accountReceivable.insurerId);
        }
      }
    }
  };

  const handleChangeAndOpenModal = async (
    actions: UseDisclosureActions,
    loading: string,
  ) => {
    if (!disabledBackButton) {
      setLoadingButtons((prev) => ({ ...prev, [loading]: true }));
      await handleSave();
      setLoadingButtons((prev) => ({ ...prev, [loading]: false }));
    }

    return actions.open();
  };

  return (
    <>
      <SpaceForButton>
        <ButtonBack path={pathWithSearch} disabled={disabledBackButton} />

        <Button
          block
          type="primary"
          disabled={
            !hasPermissions([Authorities.FINANCIAL_ACCOUNT_RECEIVABLE_ADD])
          }
          onClick={() =>
            history.push(
              '/app/financial/billToReceiver/releaseBillToReceiver/create',
            )
          }
        >
          Nova conta a receber
        </Button>

        {installment?.situation === AccountReceivableSituation.open &&
          accountReceivable?.installmentReceivables.length === 1 &&
          !isStoneOrder && (
            <AccountReceivableSplitInstallment
              handleChangeAndOpenModal={handleChangeAndOpenModal}
              loading={loadingButtons.parcel}
            />
          )}

        {installment?.situation === AccountReceivableSituation.open &&
          (accountReceivable
            ? accountReceivable.installmentReceivables.length
            : 0) > 1 &&
          !isStoneOrder && (
            <AccountReceivableRenegotiateInstallment
              handleChangeAndOpenModal={handleChangeAndOpenModal}
              loading={loadingButtons.renegotiate}
            />
          )}

        {installment?.situation === AccountReceivableSituation.open ? (
          <Dropdown.Button
            disabled={
              !hasPermissions([Authorities.FINANCIAL_ACCOUNT_RECEIVABLE_SETTLE])
            }
            overlay={
              <Menu
                disabled={isStoneOrder}
                items={[
                  {
                    key: '1',
                    label: intl.formatMessage({
                      id: 'release.form.button.partiallySettle',
                    }),
                    onClick: () =>
                      handleChangeAndOpenModal(
                        partiallySettleAction,
                        'settleInstallment',
                      ),
                  },
                ]}
              />
            }
            icon={<FaAngleDown />}
            type="primary"
            loading={loadingButtons.settleInstallment}
            onClick={() =>
              handleChangeAndOpenModal(singleSettleAction, 'settleInstallment')
            }
          >
            {intl.formatMessage({
              id: 'release.form.button.settleInstallment',
            })}
          </Dropdown.Button>
        ) : (
          <Button
            disabled={
              !hasPermissions([
                Authorities.FINANCIAL_ACCOUNT_RECEIVABLE_REFUND_INSTALLMENT,
              ])
            }
            block
            type="primary"
            onClick={reverseInstallmentAction.open}
          >
            Estornar Parcela
          </Button>
        )}

        {bankAccount?.bank?.integration &&
          installment?.situation === AccountReceivableSituation.open &&
          !installment.hasInstallmentReceivableBill && (
            <Button
              block
              type="primary"
              loading={
                customerMutation.isLoading || generateBulletMutation.isLoading
              }
              onClick={handleGenerateBullet}
            >
              Incluir boleto
            </Button>
          )}

        {!!listGrouping.data?.length && (
          <Button block type="primary" onClick={modalListGroupingAction.open}>
            Contas Agrupadas
          </Button>
        )}

        <AccountReceivableTransactionCancel />
      </SpaceForButton>

      <AccountReceivableSingleInstallmentModal
        onCancel={singleSettleAction.close}
        open={singleSettleOpened}
        data={{
          installmentReceivableId: installment?.id,
          id: installment?.accountReceivableId,
          installmentValue: installment?.installmentValue,
          bankAccountId: installment?.bankAccountId,
          discountIncrease: installment?.discountIncrease,
          documentTypeId: installment?.documentTypeId,
          currentSituation: installment?.situation,
          rateValue: installment?.rateValue,
        }}
      />

      <AccountReceivablePartiallyInstallmentModal
        onCancel={partiallySettleAction.close}
        open={partiallySettleOpened}
        data={{
          installmentReceivableId: installment?.id,
          id: installment?.accountReceivableId,
          installmentValue: installment?.installmentValue,
          bankAccountId: installment?.bankAccountId,
          discountIncrease: installment?.discountIncrease,
          documentTypeId: installment?.documentTypeId,
          currentSituation: installment?.situation,
          rateValue: installment?.rateValue,
        }}
      />

      <AccountReceivableReverseInstallmentModal
        open={reverseInstallmentOpened}
        onCancel={reverseInstallmentAction.close}
        data={{
          id: installment?.accountReceivableId,
          installmentReceivableId: installment?.id,
        }}
      />

      <CustomerSimplifiedRegisterModal
        visible={customerSimplifiedRegisterOpened}
        handleVisible={customerSimplifiedRegisterAction.toggle}
        customerId={accountReceivable?.customerId}
        onFinishCallback={() =>
          handleVerifyEmail(accountReceivable?.customerId)
        }
      />

      <InsurerSimplifiedRegisterModal
        visible={insurerSimplifiedRegisterOpened}
        handleVisible={insurerSimplifiedRegisterAction.toggle}
        insurer={insurerMutation.data}
        onFinishCallback={() =>
          handleVerifyEmail(undefined, accountReceivable?.insurerId)
        }
      />

      <AccountReceivableListGroupingModal
        onCancel={modalListGroupingAction.close}
        open={modalListGrouping}
        listGroupingData={listGrouping.data}
      />
    </>
  );
};

export default AccountReceivableFooter;
